import { createSelector } from "@reduxjs/toolkit";
import type { IPixPolygon } from "../../../../../components/PixBimViewerV2/types";
import { openapi } from "../../../../../../openapi";
import { IRootState } from "../../../../../store";
import { selectProjectNo } from "../../../../projectSelectors";
import { sortArrayByString } from "../../../../utils";
import { ISSUE_STATES_OPTIONS } from "../../issueConstants";
import { selectFilteredIssuesOnBoard, selectIssuesWithAllEnrichedData, selectMcbimSpacesQuery } from "../../issueSelectors";

export const selectFloorplanState = (state: IRootState) => state.issueReducerNew.floorplan;
export const selectSelectedFloor = createSelector([selectFloorplanState], (floorplan) => floorplan.selectedFloor);
export const selectSelectedFloorSpaceId = createSelector(
  [selectFloorplanState],
  (floorplan) => floorplan.selectedFloorSpaceId,
);
export const selectRoomSearchText = createSelector([selectFloorplanState], (floorplan) => floorplan.roomSearchText);
export const selectFloorSearchText = createSelector([selectFloorplanState], (floorplan) => floorplan.floorSearchText);
export const selectShowOnlyFloorsWithIssues = createSelector(
  [selectFloorplanState],
  (floorplan) => floorplan.showOnlyFloorsWithIssues,
);
export const selectFloorplanRotation = createSelector([selectFloorplanState], (floorplan) => floorplan.floorplanRotation);

export const selectMcbimSpacesOnSelectedFloorArgs = createSelector(
  [selectProjectNo, selectSelectedFloor],
  (projectNo, selectedFloor) => {
    if (projectNo == null || selectedFloor.trim() === "") return null;

    return {
      projectNo,
      floorName: selectedFloor,
    };
  },
);

export const selectHttpMcbimSpacesOnSelectedFloor = createSelector(
  [(state: IRootState) => state, selectMcbimSpacesOnSelectedFloorArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimSpacesOnFloor.select(args)(state);
    return q.data ?? [];
  },
);

export const selectSelectedFloorSpace = createSelector(
  [selectHttpMcbimSpacesOnSelectedFloor, selectSelectedFloorSpaceId],
  (spaces, selectedFloorSpaceId) => spaces.find((space) => space._id === selectedFloorSpaceId) ?? null,
);

export const selectConnectedBimSpaceForSelectedFloorSpace = createSelector(
  [selectMcbimSpacesQuery, selectSelectedFloorSpaceId],
  ({ data }, selectedFloorSpaceId) =>
    data.find((space) => String(space.spaceDbId) === selectedFloorSpaceId) ?? null,
);

export const selectIssuesForSelectedFloorSpace = createSelector(
  [selectFilteredIssuesOnBoard, selectSelectedFloorSpaceId],
  (issues, selectedFloorSpaceId) => {
    if (selectedFloorSpaceId === "") {
      return [];
    }

    return issues
      .filter((issue) => (issue.connectedBimSpaces ?? []).some((space) => space.spaceDbId === selectedFloorSpaceId))
      .map((issue) => ({
        _id: issue._id,
        id: issue.id,
        status: issue.status,
        title: issue.title,
        statusColor: ISSUE_STATES_OPTIONS.find((item) => item.value === issue.status)?.color ?? "gray",
      }));
  },
);

export const selectHiddenIssueCountForSelectedFloorSpace = createSelector(
  [selectIssuesWithAllEnrichedData, selectFilteredIssuesOnBoard, selectSelectedFloorSpaceId],
  (allIssues, visibleIssues, selectedFloorSpaceId) => {
    if (selectedFloorSpaceId === "") {
      return 0;
    }

    const allIssueIdsForSpace = new Set(
      allIssues
        .filter((issue) => (issue.connectedBimSpaces ?? []).some((space) => space.spaceDbId === selectedFloorSpaceId))
        .map((issue) => issue._id),
    );

    const visibleIssueIdsForSpace = new Set(
      visibleIssues
        .filter((issue) => (issue.connectedBimSpaces ?? []).some((space) => space.spaceDbId === selectedFloorSpaceId))
        .map((issue) => issue._id),
    );

    let hiddenCount = 0;

    for (const issueId of allIssueIdsForSpace) {
      if (!visibleIssueIdsForSpace.has(issueId)) {
        hiddenCount += 1;
      }
    }

    return hiddenCount;
  },
);

export const selectHttpMcbimFurnitureAndWallsOnSelectedFloor = createSelector(
  [(state: IRootState) => state, selectMcbimSpacesOnSelectedFloorArgs],
  (state, args) => {
    if (!args) return [];
    const q = openapi.endpoints.getMcbimFurnitureAndWallsOnFloor.select(args)(state);
    return q.data ?? [];
  },
);

const FURNITURE_AND_WALL_STYLE: Record<
  "IfcFurniture" | "IfcWall" | "IfcWallStandardCase",
  { fillColor: string; strokeColor?: string; strokeWidth?: string }
> = {
  IfcFurniture: { fillColor: "#E6E1D6", strokeColor: undefined, strokeWidth: undefined },
  IfcWall: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
  IfcWallStandardCase: { fillColor: "#9AA0A6", strokeColor: "#9AA0A6", strokeWidth: "1.5" },
};

const bboxToPolygon = (bbox?: number[][]): number[][] | null => {
  if (!Array.isArray(bbox) || bbox.length < 2) return null;

  if (bbox.length >= 4) {
    const points = bbox.filter((point) => Array.isArray(point) && point.length >= 2).map((point) => [point[0], point[1]]);
    return points.length >= 3 ? points : null;
  }

  const [minPoint, maxPoint] = bbox;
  if (!Array.isArray(minPoint) || !Array.isArray(maxPoint) || minPoint.length < 2 || maxPoint.length < 2) return null;

  const [minX, minY] = minPoint;
  const [maxX, maxY] = maxPoint;

  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
};

export const selectUniqueMcbimFloors = createSelector([selectMcbimSpacesQuery], ({ data }) => {
  const uniqueFloors = Array.from(new Set(data.map((space) => space.floor?.trim()).filter((floor) => Boolean(floor))));
  return sortArrayByString(
    uniqueFloors.map((floor) => ({ floor })),
    "floor",
  ).map((item) => item.floor);
});

export const selectPixPolygonsForSelectedFloor = createSelector(
  [
    selectHttpMcbimSpacesOnSelectedFloor,
    selectFilteredIssuesOnBoard,
    selectSelectedFloor,
    selectSelectedFloorSpaceId,
    selectRoomSearchText,
  ],
  (data, issues, selectedFloor, selectedFloorSpaceId, roomSearchText): IPixPolygon[] => {
    const issueCountBySpaceId = new Map<string, number>();
    const roomQuery = roomSearchText.trim().toLowerCase();

    for (const issue of issues) {
      const linkedSpacesOnFloor = new Set(
        (issue.connectedBimSpaces ?? [])
          .filter((space) => (space.floor ?? "").trim() === selectedFloor.trim())
          .map((space) => space.spaceDbId)
          .filter(Boolean),
      );

      for (const spaceId of linkedSpacesOnFloor) {
        issueCountBySpaceId.set(spaceId, (issueCountBySpaceId.get(spaceId) ?? 0) + 1);
      }
    }

    return data
      .filter((space) => Array.isArray(space.polygon) && space.polygon.length > 2)
      .map((space) => {
        const issueCount = issueCountBySpaceId.get(space._id) ?? 0;
        const hasIssues = issueCount > 0;
        const isSelected = space._id === selectedFloorSpaceId;
        const isSearchHit =
          roomQuery !== "" &&
          [space.name ?? "", space.longName ?? ""].some((value) => value.toLowerCase().includes(roomQuery));
        const baseTooltip = [space.name, space.longName].filter(Boolean).join(" - ");

        return {
          id: space._id,
          entityId: space._id,
          points: space.polygon!,
          fillColor: isSearchHit ? "#E5DBFF" : hasIssues ? "#FFF3BF" : "white",
          strokeColor: isSelected ? "#1C7ED6" : isSearchHit ? "#7B61FF" : hasIssues ? "#F08C00" : "gray",
          strokeWidth: isSelected ? "5" : isSearchHit ? "3.5" : hasIssues ? "2" : "1.5",
          text: {
            show: true,
            values: [space.name ?? "", space.longName ?? ""].filter(Boolean),
            color: "#1f2a2a",
            fontSize: "1",
          },
          tooltip:
            issueCount > 0
              ? `${baseTooltip}${baseTooltip ? " | " : ""}${issueCount} sak${issueCount === 1 ? "" : "er"}`
              : baseTooltip || undefined,
          isMoveable: false,
          isDragSelectable: false,
          opacity: 1,
        };
      });
  },
);

export const selectPixPolygonsForSelectedFloorFurnitureAndWalls = createSelector(
  [selectHttpMcbimFurnitureAndWallsOnSelectedFloor],
  (data): IPixPolygon[] => {
    const polygons: IPixPolygon[] = [];

    for (const item of data) {
      const points = bboxToPolygon(item.bbox);
      if (!points) continue;

      const style = FURNITURE_AND_WALL_STYLE[item.instanceClass];

      polygons.push({
        id: `fw-${item._id}`,
        entityId: item._id,
        points,
        fillColor: style.fillColor,
        strokeColor: style.strokeColor,
        strokeWidth: style.strokeWidth,
        text: {
          show: false,
          values: [],
          color: "#1f2a2a",
          fontSize: "1",
        },
        tooltip: [item.name, item.longName].filter(Boolean).join(" - ") || undefined,
        isMoveable: false,
        isDragSelectable: false,
        opacity: 1,
      });
    }

    return polygons;
  },
);

export const selectAllPixPolygonsForSelectedFloor = createSelector(
  [selectPixPolygonsForSelectedFloorFurnitureAndWalls, selectPixPolygonsForSelectedFloor],
  (furnitureAndWallPolygons, spacePolygons): IPixPolygon[] => {
    const furniturePolygons = furnitureAndWallPolygons.filter((polygon) => polygon.fillColor === "#E6E1D6");
    const nonFurniturePolygons = furnitureAndWallPolygons.filter((polygon) => polygon.fillColor !== "#E6E1D6");

    return [...nonFurniturePolygons, ...spacePolygons, ...furniturePolygons];
  },
);

export const selectMcbimFloorItems = createSelector(
  [selectUniqueMcbimFloors, selectFilteredIssuesOnBoard],
  (floors, issues) => {
    const issueCountByFloor = new Map<string, number>();

    for (const issue of issues) {
      const floorsForIssue = new Set(
        (issue.connectedBimSpaces ?? [])
          .map((space) => space.floor?.trim())
          .filter((floor): floor is string => Boolean(floor)),
      );

      for (const floor of floorsForIssue) {
        issueCountByFloor.set(floor, (issueCountByFloor.get(floor) ?? 0) + 1);
      }
    }

    return floors.map((floor) => ({
      floor,
      issueCount: issueCountByFloor.get(floor) ?? 0,
    }));
  },
);

export const selectFilteredMcbimFloors = createSelector(
  [selectMcbimFloorItems, selectFloorSearchText, selectShowOnlyFloorsWithIssues],
  (floors, floorSearchText, showOnlyFloorsWithIssues) => {
    const query = floorSearchText.trim().toLowerCase();
    const visibleFloors = showOnlyFloorsWithIssues ? floors.filter((item) => item.issueCount > 0) : floors;

    if (query === "") {
      return visibleFloors;
    }

    return visibleFloors.filter((item) => item.floor.toLowerCase().includes(query));
  },
);
